import { useState } from "react";
import { ApiError, api } from "../api.js";

/**
 * Продление окна протокола — одно из двух действий, разрешённых над идущим экспериментом
 * (второе — завести преемника).
 *
 * ⚠️ Компонент ОДИН на два места: карточка цели (пока протокол идёт) и блок «Решение»
 * на «Сегодня» (когда окно кончилось и система просит исход). Копия формы означала бы
 * две разные проверки «дата строго вперёд» и два разных текста про причину — а расхождение
 * здесь читается владельцем как «приложение само не знает своих правил».
 *
 * Окно двигается только ВПЕРЁД: укорочение переписало бы приверженность задним числом
 * ровно так же, как смена ритма — те же касания поделились бы на меньшее число
 * запланированных, и процент вырос бы сам. Причина обязательна: через месяц «почему
 * я продлил» не восстановить, а разбор держится именно на этом.
 */
export function ExtendWindow({
  protocolId,
  windowEnd,
  onChanged
}: {
  protocolId: string;
  windowEnd: string;
  onChanged: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function extend() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/protocols/${protocolId}/extend`, { window_end: until, reason: why });
      setOpen(false);
      setUntil("");
      setWhy("");
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "не продлилось");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="linklike extend-open" onClick={() => setOpen(true)}>
        Продлить окно
      </button>
    );
  }

  return (
    <div className="extend">
      <label>
        новое окончание окна
        <input type="date" value={until} min={nextDay(windowEnd)} onChange={(event) => setUntil(event.target.value)} />
      </label>
      <label>
        почему продлеваешь
        <textarea
          rows={2}
          value={why}
          placeholder="что помешало и что изменится за это время"
          onChange={(event) => setWhy(event.target.value)}
        />
      </label>
      <div className="choices">
        <button
          className="btn primary"
          disabled={busy || !until || why.trim().length < 5}
          onClick={() => void extend()}
        >
          Продлить
        </button>
        <button className="btn ghost" disabled={busy} onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
      <p className="outcome-note">
        Окно двигается только вперёд: укоротить его значит переписать приверженность задним
        числом. Ритм и правило успеха не меняются — это тот же эксперимент, просто длиннее.
      </p>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Следующий день после конца окна: продлевать «на ту же дату» нечего. */
function nextDay(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return day;
  const next = new Date(year, month - 1, date + 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
}
